import { useMemo } from "react";
import * as d3 from "d3";
import { MARGIN } from "./constants";
import styles from "./renderer.module.css";
import React from "react";
import Dashboard from "../../model/Dashboard";
import {AllUSElectionData} from "../../model/data/StateElectionDataEntry"
import {USMapInteractionData} from "../geographic/USMapToolTip";


type RendererProps = {
  width: number;
  height: number;
  data: AllUSElectionData[];
  setHoveredCell: (hoveredCell: USMapInteractionData | null) => void;
  // colorScale: d3.ScaleLinear<string, string, never>;
  yScale: d3.ScaleBand<string>;  // Pass yScale as a prop
  dashboard:Dashboard|null;
  updateSelectedStates: (states: Set<string>) => void;
  updateSelectedYear: (year: number) => void;

};
const normalizeStateName = (name: string | undefined | null): string => {
  if (typeof name !== 'string') {
    return ''; // Return an empty string or handle the error as needed
  }
  return name.replace(/\s/g, '');
};

export const Renderer = ({
  width,
  height,
  data,
  setHoveredCell,
  // colorScale,
  yScale,  // Receive yScale as a prop
  dashboard,
  updateSelectedStates,
  updateSelectedYear
}: RendererProps) => {
  const boundsWidth = width - MARGIN.right - MARGIN.left;
  const boundsHeight = height - MARGIN.top - MARGIN.bottom;

  const allXGroups = useMemo(
    () => [...new Set(data.map((d) => String(d.year)))],
    [data]
  );

  const xScale = useMemo(() => {
    return d3
      .scaleBand()
      .range([0, boundsWidth])
      .domain(allXGroups)
      .padding(0.1);
  }, [allXGroups, boundsWidth]);

  const allRects = data.map((d, i) => {
    // console.log(String(d.year))
    const xPos = xScale(String(d.year));
    const yPos = yScale(d.state_name);

    if (d.point_lead === null || !xPos || !yPos) {
      return null;
    }

    const value = d.point_lead !== 0 ? d.point_lead * (d.winner === 'dem' ? -1 : 1) : 0.1 * (d.winner === 'dem' ? -1 : 1);

    // // Highlight column if the year matches the selected year
    // const isYearSelected = dashboard?.year === d.year;

    // // Highlight row if the state is in the selected states set
    // const isStateSelected = dashboard?.selectedStates.has(normalizeStateName(d.state));

    function addSpaceBetweenLowerAndUpper(text: string): string {
      return text.replace(/([a-z])([A-Z])/g, "$1 $2");
    }

    return (
      <rect
        key={i}
        x={xPos}
        y={yPos}
        className={styles.rectangle}
        width={xScale.bandwidth()}
        height={yScale.bandwidth()}
        fill={value  ? dashboard?.colorScale(value) : (d.winner === 'dem' ? dashboard?.colorScale(-0.1) : (d.winner === 'rep' ? dashboard?.colorScale(0.1): "#F8F8F8"))}//"#F8F8F8"}
        onMouseOver={(e) => {
            const usMapInteractionData: USMapInteractionData = {
                data: [
                    `${addSpaceBetweenLowerAndUpper(d.state_name)}: ${d.winner} won by ${d.point_lead}%`,
                    `Total votes: ${Math.round(d.total_votes/1000)}k`,
                    `Dem percentage: ${d.dem_percentage}%`,
                    `Rep percentage: ${d.rep_percentage}%`,
                    `Dem electoral votes: ${d.dem_electoral_votes}`,
                    `Rep electoral votes: ${d.rep_electoral_votes}`,
                    `Number of voters per electoral vote: ${Math.round(d.num_voters_per_electoral_vote/1000)}k`,
                    `Year: ${d.year}`
                ],
                value: value,
                position: { x: e.clientX, y: e.clientY }
                };

          setHoveredCell(usMapInteractionData);
          
        }}
        onMouseOut={() => {
          setHoveredCell(null);
          
        }}
        onClick = {() => {
          const stateName = normalizeStateName(d.state_name);

          if (dashboard?.selectedStates.has(stateName)) {
              dashboard?.selectedStates.delete(stateName);
          } else {
              dashboard?.selectedStates.add(stateName);
          }
          if (dashboard){
            updateSelectedStates(dashboard?.selectedStates);}
          
          if ( d.year) {
            // console.log(d.year)
            dashboard?.setYear(d.year);
            updateSelectedYear(d.year)}
          // } else {
          //     dashboard?.selectedStates.add(stateName);
          // }
          // if (dashboard){
          //   updateSelectedStates(dashboard?.selectedStates);}
          
      }
    }
      />
    );
  });

    // Highlight rows for selected states
    const highlightRows = dashboard?.selectedStates
    ? [...dashboard.selectedStates].map((stateName, i) => {
        const yPos = yScale(stateName);
        return (
          yPos && (
            <rect
              key={`highlight-row-${stateName}-${i}`}
              x={0}
              y={yPos}
              width={boundsWidth}
              height={yScale.bandwidth()}
              fill="none"
              stroke="black"
              strokeWidth={2}
              pointerEvents="none"
              
            />
          )
        );
      })
    : null;

     // Highlight columns for selected year
  const highlightColumn = dashboard?.year
  ? (() => {
      const xPos = xScale(String(dashboard.year));
      return (
        xPos && (
          <rect
            key={`highlight-column-${dashboard.year}`}
            x={xPos}
            y={0}
            width={xScale.bandwidth()}
            height={boundsHeight - 30}
            fill="none"
            stroke="black"
            strokeWidth={2}
            pointerEvents="none"
          />
        )
      );
    })()
  : null;


  const xLabels = allXGroups.map((name, i) => {
    return (
      <text
        key={i}
        x={xScale(name)! + xScale.bandwidth() / 2}
        y={boundsHeight-10}  // Move the labels below the chart
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={10}
        fill="black"
        transform={`rotate(45, ${xScale(name)! + xScale.bandwidth() / 2}, ${boundsHeight})`} // Rotate 45 degrees
        style={{ cursor: 'pointer' }}  // Make it look clickable
        onClick={() => {
          dashboard?.setYear(Number(name));
          updateSelectedYear(Number(name));
        }}
      >
        {name}
      </text>
    );
  });
  // console.log(xLabels)

  return (
    <svg width={width} height={height+40} >
      <g
        width={boundsWidth}
        height={boundsHeight}
        transform={`translate(${MARGIN.left}, ${MARGIN.top})`}
      >
        {/* Render highlight columns and rows */}

        {allRects}
        {highlightColumn}
        {highlightRows}
      </g>

      {/* Render the xLabels below the heatmap */}
      <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
        {xLabels}
      </g>
    </svg>
  );
};
